import { action, computed, makeAutoObservable, observable, ObservableSet } from "mobx";

// ────────────────────────────────────────────
// ColumnSelectionStore
//
// Pure multi-column selection state.  Knows nothing about the sidebar,
// column configs, or the grid — it just tracks which field keys are
// currently selected via CMD+Click.
//
// Owned by the root ColumnConfigStore and exposed as `store.selection`.
// ────────────────────────────────────────────

export class ColumnSelectionStore {
  /** Set of field keys that are currently multi-selected. */
  readonly items: ObservableSet<string> = observable.set<string>();

  /** The field key most recently toggled *into* the selection (not removed). */
  lastToggled: string | null = null;

  constructor() {
    makeAutoObservable(this, {
      items: observable,
      lastToggled: observable,
      toggle: action,
      add: action,
      remove: action,
      setSelection: action,
      clear: action,
      count: computed,
      hasSelection: computed,
      fields: computed,
    });
  }

  // ── Mutations ─────────────────────────────

  /** Add a field if absent, remove it if present. */
  toggle = (field: string): void => {
    if (this.items.has(field)) {
      this.items.delete(field);
      // If the removed field was the last toggled, fall back to the most
      // recently remaining item (or null if the set is now empty).
      if (this.lastToggled === field) {
        const remaining = [...this.items];
        this.lastToggled = remaining.length > 0 ? remaining[remaining.length - 1] : null;
      }
    } else {
      this.items.add(field);
      this.lastToggled = field;
    }
  };

  /** Add a single field to the selection. */
  add = (field: string): void => {
    this.items.add(field);
    this.lastToggled = field;
  };

  /** Remove a single field from the selection. */
  remove = (field: string): void => {
    this.items.delete(field);
  };

  /** Replace the selection with a de-duplicated field list. */
  setSelection = (fields: readonly string[], lastToggled?: string | null): void => {
    this.items.clear();

    const uniqueFields = Array.from(new Set(fields));
    uniqueFields.forEach(field => this.items.add(field));

    if (lastToggled && this.items.has(lastToggled)) {
      this.lastToggled = lastToggled;
      return;
    }

    this.lastToggled = uniqueFields[uniqueFields.length - 1] ?? null;
  };

  /** Clear every selected field. */
  clear = (): void => {
    this.items.clear();
    this.lastToggled = null;
  };

  // ── Queries ───────────────────────────────

  /** Whether `field` is currently selected. */
  has = (field: string): boolean => this.items.has(field);

  /** Number of selected columns. */
  get count(): number {
    return this.items.size;
  }

  /** Whether at least one column is selected. */
  get hasSelection(): boolean {
    return this.items.size > 0;
  }

  /** Snapshot of selected field keys as a plain array. */
  get fields(): string[] {
    return [...this.items];
  }
}
